/**
 * Permission keys mirrored from apps/backend permission.constants.ts.
 * Keep in sync when seeders add new featureArea values.
 */
export const M1_PERMISSIONS = {
  // Control Library
  READ_CONTROL_LIBRARY: 'READ_CONTROL_LIBRARY',
  READ_CONTROL_FRAMEWORK_FILTER: 'READ_CONTROL_FRAMEWORK_FILTER',
  READ_CONTROL_STATUS_HISTORY: 'READ_CONTROL_STATUS_HISTORY',
  READ_FRAMEWORK_CROSS_MAPPING: 'READ_FRAMEWORK_CROSS_MAPPING',

  // Control Ownership
  UPDATE_CONTROL_OWNERSHIP: 'UPDATE_CONTROL_OWNERSHIP',

  // SOP-to-Control Mapping
  CREATE_SOP_CONTROL_MAPPING: 'CREATE_SOP_CONTROL_MAPPING',
  CREATE_SOP_CONTROL_MAPPING_SUGGEST: 'CREATE_SOP_CONTROL_MAPPING_SUGGEST',
  APPROVE_SOP_CONTROL_MAPPING: 'APPROVE_SOP_CONTROL_MAPPING',
  READ_SOP_CONTROL_MAPPING: 'READ_SOP_CONTROL_MAPPING',

  // Framework Gap & Reporting
  READ_FRAMEWORK_GAP_REPORT: 'READ_FRAMEWORK_GAP_REPORT',
  CREATE_GAP_TO_TASK_CONVERSION: 'CREATE_GAP_TO_TASK_CONVERSION',

  // Statement of Applicability
  UPDATE_STATEMENT_OF_APPLICABILITY: 'UPDATE_STATEMENT_OF_APPLICABILITY',
  READ_STATEMENT_OF_APPLICABILITY: 'READ_STATEMENT_OF_APPLICABILITY',
  CREATE_CONTROL_NA_JUSTIFICATION: 'CREATE_CONTROL_NA_JUSTIFICATION',
  APPROVE_CONTROL_NA_JUSTIFICATION: 'APPROVE_CONTROL_NA_JUSTIFICATION',

  // Register Sign-off
  CREATE_CONTROL_REGISTER_SIGNOFF_REQUEST: 'CREATE_CONTROL_REGISTER_SIGNOFF_REQUEST',
  APPROVE_CONTROL_REGISTER_SIGNOFF: 'APPROVE_CONTROL_REGISTER_SIGNOFF',

  // Control Testing
  CREATE_CONTROL_TEST_EXECUTION: 'CREATE_CONTROL_TEST_EXECUTION',
  READ_CONTROL_TEST_HISTORY: 'READ_CONTROL_TEST_HISTORY',
  UPDATE_CONTROL_HEALTH_STATUS: 'UPDATE_CONTROL_HEALTH_STATUS',
} as const;

export const M2_PERMISSIONS = {
  // Audit
  CREATE_AUDIT: 'CREATE_AUDIT',
  READ_AUDIT_REGISTER: 'READ_AUDIT_REGISTER',
  UPDATE_AUDIT: 'UPDATE_AUDIT',

  // Auditor Provisioning
  CREATE_AUDIT_AUDITOR_PROVISION: 'CREATE_AUDIT_AUDITOR_PROVISION',

  // Audit Request Items
  CREATE_AUDIT_REQUEST_ITEM: 'CREATE_AUDIT_REQUEST_ITEM',
  UPDATE_AUDIT_REQUEST_ITEM_ASSIGNMENT: 'UPDATE_AUDIT_REQUEST_ITEM_ASSIGNMENT',
  UPDATE_AUDIT_REQUEST_ITEM_STATUS: 'UPDATE_AUDIT_REQUEST_ITEM_STATUS',

  // Request Item Evidence
  CREATE_AUDIT_REQUEST_ITEM_EVIDENCE: 'CREATE_AUDIT_REQUEST_ITEM_EVIDENCE',

  // Audit Pack
  CREATE_AUDIT_PACK_SHARE: 'CREATE_AUDIT_PACK_SHARE',
  READ_AUDIT_ENGAGEMENT_PACK: 'READ_AUDIT_ENGAGEMENT_PACK',

  // Export Requests
  CREATE_AUDIT_EXPORT_REQUEST: 'CREATE_AUDIT_EXPORT_REQUEST',

  // Audit Reports
  CREATE_AUDIT_REPORT: 'CREATE_AUDIT_REPORT',

  // Findings
  CREATE_AUDIT_FINDING: 'CREATE_AUDIT_FINDING',
  APPROVE_AUDIT_FINDING_ACKNOWLEDGEMENT: 'APPROVE_AUDIT_FINDING_ACKNOWLEDGEMENT',
  CREATE_AUDIT_FINDING_RISK_CONVERSION: 'CREATE_AUDIT_FINDING_RISK_CONVERSION',
  UPDATE_AUDIT_FINDING_REOPEN: 'UPDATE_AUDIT_FINDING_REOPEN',
  CREATE_AUDIT_MINOR_FINDING_CLOSURE: 'CREATE_AUDIT_MINOR_FINDING_CLOSURE',
  UPDATE_AUDIT_FINDING_CLOSURE_DECISION: 'UPDATE_AUDIT_FINDING_CLOSURE_DECISION',

  // Corrective Action Plans
  CREATE_AUDIT_FINDING_CAP: 'CREATE_AUDIT_FINDING_CAP',
  APPROVE_AUDIT_FINDING_CAP: 'APPROVE_AUDIT_FINDING_CAP',
  CREATE_AUDIT_CORRECTIVE_ACTION_EVIDENCE: 'CREATE_AUDIT_CORRECTIVE_ACTION_EVIDENCE',
  UPDATE_AUDIT_CORRECTIVE_ACTION_VERIFICATION: 'UPDATE_AUDIT_CORRECTIVE_ACTION_VERIFICATION',

  // Export
  UPDATE_AUDIT_EXPORT_DECISION: 'UPDATE_AUDIT_EXPORT_DECISION',

  // MRM
  CREATE_AUDIT_MRM_PACK: 'CREATE_AUDIT_MRM_PACK',

  // KPI
  READ_AUDIT_KPI: 'READ_AUDIT_KPI',

  // MRM meetings (/m2/mrm/meetings)
  READ_AUDIT_MRM_MEETING: 'READ_AUDIT_MRM_MEETING',
  CREATE_AUDIT_MRM_MEETING: 'CREATE_AUDIT_MRM_MEETING',
  UPDATE_AUDIT_MRM_MEETING: 'UPDATE_AUDIT_MRM_MEETING',
  DELETE_AUDIT_MRM_MEETING: 'DELETE_AUDIT_MRM_MEETING',
} as const;

export const M3_PERMISSIONS = {
  // Risk Register & Taxonomy
  READ_RISK_REGISTER: 'READ_RISK_REGISTER',
  CREATE_RISK: 'CREATE_RISK',
  UPDATE_RISK: 'UPDATE_RISK',
  MANAGE_RISK_TAXONOMY: 'MANAGE_RISK_TAXONOMY',

  // Risk Scoring & Appetite
  CREATE_RISK_SCORE: 'CREATE_RISK_SCORE',
  READ_RISK_SCORE_HISTORY: 'READ_RISK_SCORE_HISTORY',
  CREATE_RISK_APPETITE_PROPOSAL: 'CREATE_RISK_APPETITE_PROPOSAL',
  APPROVE_RISK_APPETITE: 'APPROVE_RISK_APPETITE',

  // Risk Treatment
  CREATE_RISK_TREATMENT_PLAN: 'CREATE_RISK_TREATMENT_PLAN',
  APPROVE_RISK_TREATMENT_PLAN: 'APPROVE_RISK_TREATMENT_PLAN',
  SUBMIT_RISK_TREATMENT_ACTION: 'SUBMIT_RISK_TREATMENT_ACTION',
  UPDATE_RISK_TREATMENT_VERIFICATION: 'UPDATE_RISK_TREATMENT_VERIFICATION',
  CREATE_RISK_CLOSURE: 'CREATE_RISK_CLOSURE',
  APPROVE_RISK_CLOSURE: 'APPROVE_RISK_CLOSURE',

  // Risk Review Cycle — CREATE_RISK_REVIEW_SWEEP/APPROVE_RISK_REVIEW_SWEEP were dead scaffolding
  // (seeded but never wired to a route) and were removed by the backend's
  // 20260904090000-rework-m3-review-permissions.js migration; READ_RISK_REVIEW_CYCLE
  // (GET /m3/risks/review-cycle) replaces them. CREATE_RISK_REVIEW_RESPONSE is now R4-only —
  // that same migration revoked R2's grant.
  READ_RISK_REVIEW_CYCLE: 'READ_RISK_REVIEW_CYCLE',
  CREATE_RISK_REVIEW_RESPONSE: 'CREATE_RISK_REVIEW_RESPONSE',

  // Reporting & Escalation
  UPDATE_RISK_ESCALATION_ACKNOWLEDGEMENT: 'UPDATE_RISK_ESCALATION_ACKNOWLEDGEMENT',
  READ_RISK_TREATMENT_STATUS_REPORT: 'READ_RISK_TREATMENT_STATUS_REPORT',
  READ_RISK_KPI: 'READ_RISK_KPI',

  // Attachments
  CREATE_RISK_ATTACHMENT: 'CREATE_RISK_ATTACHMENT',
  DELETE_RISK_ATTACHMENT: 'DELETE_RISK_ATTACHMENT',
} as const;

export const M4_PERMISSIONS = {
  READ_DOCUMENT_REGISTRY: 'READ_DOCUMENT_REGISTRY',
  CREATE_DOCUMENT_IMPORT: 'CREATE_DOCUMENT_IMPORT',
  UPDATE_DOCUMENT_METADATA: 'UPDATE_DOCUMENT_METADATA',
  UPDATE_DOCUMENT_ACCESS_LEVEL: 'UPDATE_DOCUMENT_ACCESS_LEVEL',
  READ_DOCUMENT_HYPERLINK: 'READ_DOCUMENT_HYPERLINK',
  READ_DOCUMENT_DOWNLOAD: 'READ_DOCUMENT_DOWNLOAD',
  UPDATE_DOWNLOAD_PERMISSION: 'UPDATE_DOWNLOAD_PERMISSION',
  CREATE_DOCUMENT_REVISION: 'CREATE_DOCUMENT_REVISION',
  UPDATE_REVISION_CLASSIFICATION: 'UPDATE_REVISION_CLASSIFICATION',
  READ_VERSION_HISTORY: 'READ_VERSION_HISTORY',
  READ_SUPERSEDED_VERSION_DOWNLOAD: 'READ_SUPERSEDED_VERSION_DOWNLOAD',
  CREATE_REVIEW_CYCLE: 'CREATE_REVIEW_CYCLE',
  UPDATE_REVIEW_CYCLE_REVIEWER: 'UPDATE_REVIEW_CYCLE_REVIEWER',
  CREATE_REVIEW_CONFIRMATION: 'CREATE_REVIEW_CONFIRMATION',
  CREATE_REVIEW_RETURN_FOR_REWORK: 'CREATE_REVIEW_RETURN_FOR_REWORK',
  CREATE_REVIEW_CYCLE_CANCELLATION: 'CREATE_REVIEW_CYCLE_CANCELLATION',
  APPROVE_MINOR_REVISION: 'APPROVE_MINOR_REVISION',
  APPROVE_MAJOR_REVISION: 'APPROVE_MAJOR_REVISION',
  READ_REVIEW_HISTORY: 'READ_REVIEW_HISTORY',
  CREATE_ACKNOWLEDGEMENT_CAMPAIGN: 'CREATE_ACKNOWLEDGEMENT_CAMPAIGN',
  READ_ACKNOWLEDGEMENT_CAMPAIGN_DASHBOARD: 'READ_ACKNOWLEDGEMENT_CAMPAIGN_DASHBOARD',
  CREATE_EMPLOYEE_ACKNOWLEDGEMENT: 'CREATE_EMPLOYEE_ACKNOWLEDGEMENT',
  READ_ACKNOWLEDGEMENT_EVIDENCE_EXPORT: 'READ_ACKNOWLEDGEMENT_EVIDENCE_EXPORT',
  CREATE_CONTROL_LINKAGE: 'CREATE_CONTROL_LINKAGE',
  READ_CONTROL_LINKAGE: 'READ_CONTROL_LINKAGE',
  READ_EVIDENCE_LINKAGE: 'READ_EVIDENCE_LINKAGE',
  READ_DOCUMENT_IMPACT_ASSESSMENT: 'READ_DOCUMENT_IMPACT_ASSESSMENT',
  READ_TRACEABILITY_CHAIN: 'READ_TRACEABILITY_CHAIN',
} as const;

export const M6_PERMISSIONS = {
  READ_EVIDENCE_REPOSITORY: 'READ_EVIDENCE_REPOSITORY',
  CREATE_EVIDENCE_UPLOAD: 'CREATE_EVIDENCE_UPLOAD',
  UPDATE_EVIDENCE_INVALID_STATUS: 'UPDATE_EVIDENCE_INVALID_STATUS',
  READ_EVIDENCE_COLLECTION_DASHBOARD: 'READ_EVIDENCE_COLLECTION_DASHBOARD',
  UPDATE_EVIDENCE_CONTROL_TAG: 'UPDATE_EVIDENCE_CONTROL_TAG',
  CREATE_EVIDENCE_PACK: 'CREATE_EVIDENCE_PACK',
  READ_EVIDENCE_PACK: 'READ_EVIDENCE_PACK',
  READ_EVIDENCE_VIEW: 'READ_EVIDENCE_VIEW',
  READ_EVIDENCE_DOWNLOAD: 'READ_EVIDENCE_DOWNLOAD',
  EXPORT_EVIDENCE_PACK: 'EXPORT_EVIDENCE_PACK',
  UPDATE_EVIDENCE_REQUIREMENT: 'UPDATE_EVIDENCE_REQUIREMENT',
  UPDATE_EVIDENCE_VALIDITY_OVERRIDE: 'UPDATE_EVIDENCE_VALIDITY_OVERRIDE',
  READ_EVIDENCE_CHAIN_OF_CUSTODY: 'READ_EVIDENCE_CHAIN_OF_CUSTODY',
  EXPORT_EVIDENCE_AUDIT_LOG: 'EXPORT_EVIDENCE_AUDIT_LOG',
  CREATE_EVIDENCE_BULK_IMPORT: 'CREATE_EVIDENCE_BULK_IMPORT',
} as const;

export const M9_PERMISSIONS = {
  READ_ROLE_MATRIX: 'READ_ROLE_MATRIX',
  UPDATE_ROLE_MATRIX: 'UPDATE_ROLE_MATRIX',
  READ_EXTERNAL_AUDITOR_REGISTRY: 'READ_EXTERNAL_AUDITOR_REGISTRY',
  CREATE_EXTERNAL_AUDITOR: 'CREATE_EXTERNAL_AUDITOR',
  UPDATE_EXTERNAL_AUDITOR: 'UPDATE_EXTERNAL_AUDITOR',
  REVOKE_EXTERNAL_AUDITOR: 'REVOKE_EXTERNAL_AUDITOR',
  READ_USER_ACCESS_AUDIT_LOG: 'READ_USER_ACCESS_AUDIT_LOG',
  EXPORT_USER_ACCESS_AUDIT_LOG: 'EXPORT_USER_ACCESS_AUDIT_LOG',
} as const;

export const PERMISSIONS = {
  ...M1_PERMISSIONS,
  ...M2_PERMISSIONS,
  ...M3_PERMISSIONS,
  ...M4_PERMISSIONS,
  ...M6_PERMISSIONS,
  ...M9_PERMISSIONS,
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
